import { CerrixPromptService } from "../../../../../shared/services/cerrix-prompt.service";
import { Component, inject, OnInit } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { DevManagementDataService } from "@app/admin/services/devmanagement.service";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { GenericEditWindowButton } from "@app/shared/models/GenericList/GenericManagerConfig";
import { SharedModule } from "@app/shared/shared.module";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/admin/i18n/scope";

@Component({
    selector: "dev-keys",
    templateUrl: "./dev-keys.component.html",
    styleUrls: ["./dev-keys.component.scss"],
    imports: [SharedModule]
})
export class DevKeysComponent implements OnInit {
    private dataService = inject(DevManagementDataService);
    private promptService = inject(CerrixPromptService);
    private _toastr = inject(ToastrService);
    private translation = injectTranslation(scope);

    config: GenericListConfig;
    isEnabled = false;
    isLoading = true;

    async ngOnInit() {
        await this.init();
    }

    async init() {
        var data = await this.dataService.getApplicationKeys();
        let idCounter = 1;
        data.forEach((i) => {
            try {
                i.id = idCounter++;
            } catch {}
        });

        await this.translation.ready();
        this.createListConfig(data);
        this.isLoading = false;
    }

    private createListConfig(data: any[]) {
        let buttonConfig = this.createButtonConfig();

        this.config = <GenericListConfig>{
            name: this.translation.t("devKeys.listName"),
            idProp: "id",
            data: data,
            allowAdd: false,
            allowEdit: false,
            allowDelete: false,
            customModalClass: "modal-xxl",
            editWindowButtons: buttonConfig,
            fields: [
                {
                    fieldName: "KeyType",
                    prettyName: this.translation.t("devKeys.keyTypeField"),
                    required: true,
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: "Updated",
                    prettyName: this.translation.t("devKeys.updatedField"),
                    required: true,
                    fieldType: GenericListFieldType.DateTime,
                    isReadonly: true,
                },

                {
                    fieldName: "Updater",
                    prettyName: this.translation.t("devKeys.updaterField"),
                    required: true,
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    fieldName: "Reason",
                    prettyName: this.translation.t("devKeys.reasonField"),
                    required: false,
                    fieldType: GenericListFieldType.TextArea,
                    isReadonly: true,
                },
            ],
        };
    }

    createButtonConfig(): GenericEditWindowButton[] {
        let generateButton = {
            text: this.translation.t("devKeys.generateNewKey"),
            class: "btn btn-warning",
            icon: "far fa-key",
            uiid: "button-generate",
            clickEvent: async (row: any) => {
                if (
                    !(await this.promptService
                        .confirm(
                            this.translation.t("devKeys.generateKeyTitle"),
                            this.translation.t("devKeys.generateKeyConfirmMessage", {
                                keyType: row.KeyType,
                            })
                        )
                        .toPromise())
                ) {
                    return false;
                }

                let randomNumber = this.getRandomInt(10000, 99999);
                let validation = await this.promptService
                    .prompt({
                        data: {
                            title: this.translation.t("devKeys.generateKeySecurityCheckTitle"),
                            message: this.translation.t("devManagement.securityCheckMessage", {
                                number: String(randomNumber),
                            }),
                            fields: [
                                {
                                    fieldType: GenericListFieldType.TextArea,
                                    fieldName: "reason",
                                    prettyName: this.translation.t("devKeys.reasonField"),
                                    required: true,
                                },
                                {
                                    fieldType: GenericListFieldType.Number,
                                    fieldName: "number",
                                    prettyName: this.translation.t("devManagement.numberField"),
                                    defaultValue: 10000,
                                    minimumValue: 10000,
                                    maximumValue: 99999,
                                    required: true,
                                },
                            ],
                        },
                    })
                    .toPromise();
                if (!validation) {
                    return false;
                }

                let number = validation.number;
                if (number != randomNumber) {
                    this._toastr.warning(this.translation.t("devManagement.invalidValidationNumber"));
                    return false;
                }

                await this.dataService.generateApplicationKey(row.KeyType, validation.reason);
                this._toastr.success(this.translation.t("devKeys.newKeyGenerated"));

                return true;
            },
        };

        return [generateButton];
    }

    getRandomInt(min: number, max: number) {
        let x = max - min;
        let rnd = Math.floor(Math.random() * x);
        return rnd + min;
    }
}
